<template>
  <component :is="loginComponentName" ref="loginComponent" @submit="handleLogin" />
</template>
<script>
import jwt_decode from "jwt-decode";
import { logEvent } from "../utils/audit-logger";

export default {
  name: "LoginWrapper",
  props: {
    site: null,
    loginComponentName: {
      type: String,
      required: true,
    },
  },
  methods: {
    siteHasCustomLogin() {
      return (this.site.components || []).some((c) => c.name === "login");
    },
    authMethodLabel(formData) {
      if (formData["authId"]) return `auth:${formData["authId"]}`;
      return "password";
    },
    logLoginFailed(reason, formData = {}) {
      const attemptEmail =
        formData.email ||
        formData.username ||
        formData.user ||
        null;
      logEvent("login", {
        outcome: "failed",
        reason,
        attemptEmail: typeof attemptEmail === "string" ? attemptEmail : null,
      });
    },
    failureMessage(reason) {
      if (reason === "network_error") return "Unable to reach the login server. Please try again.";
      if (reason === "invalid_response") return "Unexpected response from the login server.";
      if (reason === "http_400" || reason === "http_401" || reason === "http_403") return "Incorrect email or password.";
      if (reason === "invalid_token" || reason === "missing_token") return "Login response was invalid. Please try again.";
      return "Login failed. Please try again.";
    },
    // Resets the login form (button/spinner, error message) after a failed attempt.
    // Only components that implement onLoginFailed (like gg-global-login) react to
    // this — a site's own custom `login` component can opt in the same way.
    notifyLoginFailure(reason, formData) {
      this.logLoginFailed(reason, formData);
      const comp = this.$refs.loginComponent;
      if (comp && typeof comp.onLoginFailed === "function") {
        comp.onLoginFailed(this.failureMessage(reason));
      }
    },
    async handleLogin(formData) {
      let authUrl = this.site.settings.authUrl;
      let method = this.site.settings.authAPIMethod;
      let customHeaders = this.site.settings.authHeaders;

      if (formData["authId"]) {
        let authDef = (this.site.settings?.extraAuths || []).find(
          (auth) => auth.key === formData["authId"],
        );
        if (authDef) {
          authUrl = authDef.url;
          method = authDef.method;
          customHeaders = authDef.headers;
        }
      }

      Object.keys(formData).forEach((key) => {
        authUrl = authUrl.replace(`:${key}`, formData[key]);
      });

      // Remap the fixed email/password fields to whatever key this endpoint expects
      // (e.g. "username"), configured per-site in Auth Settings.
      const body = { ...formData };
      const emailParam = this.site.settings.authEmailParam;
      const passwordParam = this.site.settings.authPasswordParam;
      if (emailParam && Object.prototype.hasOwnProperty.call(body, "email")) {
        body[emailParam] = body.email;
        if (emailParam !== "email") delete body.email;
      }
      if (passwordParam && Object.prototype.hasOwnProperty.call(body, "password")) {
        body[passwordParam] = body.password;
        if (passwordParam !== "password") delete body.password;
      }

      const fetchParam = {
        headers: (customHeaders || []).reduce(
          (acc, h) => {
            if (h.key) acc[h.key] = h.value;
            return acc;
          },
          { "Content-Type": "application/json" },
        ),
      };
      if (method) {
        fetchParam["method"] = method;
      }
      if (method === "POST") {
        fetchParam["body"] = JSON.stringify(body);
      }

      const authMethod = this.authMethodLabel(formData);

      let response;
      try {
        response = await fetch(authUrl, fetchParam);
      } catch {
        this.notifyLoginFailure("network_error", formData);
        return;
      }

      let resp;
      try {
        resp = await response.json();
      } catch {
        this.notifyLoginFailure("invalid_response", formData);
        return;
      }

      if (!response.ok) {
        this.notifyLoginFailure(`http_${response.status}`, formData);
        return;
      }

      const authResponse = this.site.settings.authResponse;
      let token;
      if (authResponse) {
        const match = authResponse.match(/{{(.*?)}}/);
        if (match) {
          const keys = match[1].trim().split(".");
          token = keys.reduce((obj, key) => obj?.[key], resp);
        }
      }
      if (!token) token = resp.token;

      let tokenKey = `gg-${this.site.uri}-token`;
      if (formData["authId"]) {
        tokenKey = `gg-${this.site.uri}-ggAuth${formData["authId"]}-token`;
      }

      if (!token && resp.success === true) {
        // Builder login: no token in body, auth is via HttpOnly cookies.
        // Store a sentinel so the login gate passes on next page load.
        window.localStorage.setItem(tokenKey, "builder-auth");
      } else if (token) {
        try {
          window.user = jwt_decode(token);
        } catch {
          this.notifyLoginFailure("invalid_token", formData);
          return;
        }
        window.localStorage.setItem(tokenKey, token);
      } else {
        this.notifyLoginFailure("missing_token", formData);
        return;
      }

      logEvent("login", { outcome: "success", reason: authMethod });

      const siteUri = window.location.pathname.slice(1).split("/")[0];

      // After global gate, show the site's own `login` component if it exists.
      if (
        this.loginComponentName === "gg-global-login" &&
        this.siteHasCustomLogin()
      ) {
        window.location.href = `/${siteUri}/login`;
        return;
      }

      window.location.href = `/${siteUri}`;
    },
  },
};
</script>